import React, { useState } from 'react';
import { 
  Building2, 
  ArrowRight, 
  CheckCircle2, 
  Users, 
  Sparkles, 
  ShieldCheck, 
  Zap, 
  Activity, 
  X, 
  Calendar, 
  Play, 
  Target, 
  Layers, 
  Lock, 
  DollarSign, 
  BarChart3, 
  Receipt,
  Globe,
  Briefcase,
  TrendingUp,
  Award,
  Clock,
  Landmark,
  FileSpreadsheet,
  Cpu,
  Mail,
  ChevronRight,
  Shield,
  HelpCircle,
  FolderLock,
  FileText,
  Check,
  Percent,
  Calculator,
  Sliders,
  Database,
  ArrowUpRight,
  AlertCircle
} from 'lucide-react';
import { RequestDemoModal, DemoInquiryType } from './RequestDemoModal';
import { InteractiveTourModal } from './InteractiveTourModal';
import { GoogleCloudPartnerBadge } from './GoogleCloudPartnerBadge';

export interface WhatIsAzuraOSModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartTrial?: () => void;
  onRequestDemo?: () => void;
  onOpenTour?: () => void;
  onNavigateToPricing?: () => void;
  initialTab?: 'pitch' | 'capabilities' | 'pilot_program';
}

export interface PilotHighlightFeature {
  id: string;
  category: 'institutions' | 'competitive' | 'retention' | 'security';
  title: string;
  badge: string;
  metric: string;
  description: string;
  pilotBenefit: string;
  icon: any;
  color: string;
}

export const PILOT_HIGHLIGHTS: PilotHighlightFeature[] = [
  {
    id: 'margin_engine',
    category: 'institutions',
    title: 'Live Gross & Net Margin Defense Engine',
    badge: 'Real-Time Solvency',
    metric: '100% Real-Time',
    description: 'Recalculates net safe spendable cash and operational runway dynamically after tax buffers and fixed overhead on every single booking and expense.',
    pilotBenefit: 'Institutions eliminate guesswork and assess true cohort solvency rather than waiting for lagged quarterly statements.',
    icon: TrendingUp,
    color: 'teal'
  },
  {
    id: 'deposit_safeguards',
    category: 'retention',
    title: '50% Milestone Deposits & Smart Invoicing',
    badge: 'Cash Flow Protection',
    metric: '92% Faster Collection',
    description: 'Standardizes professional PDF tax invoicing with enforceable 50% milestone deposits and automated client follow-ups.',
    pilotBenefit: 'Drastically prevents early SME insolvency and client payment default across sponsored founder cohorts.',
    icon: Receipt,
    color: 'emerald'
  },
  {
    id: 'global_tax_engine',
    category: 'institutions',
    title: 'Multi-Jurisdiction Tax & Nil Return Hub',
    badge: 'Global Compliance',
    metric: '6 Global Jurisdictions',
    description: 'Pre-calibrated fiscal engines and 1-click Nil Return packs for US (Schedule C), UK (HMRC), South Africa (SARS), EU (VAT), Australia (ATO), and India (GST/44ADA).',
    pilotBenefit: 'Cohort members remain audit-ready and tax compliant from Day 1 without requiring expensive outsourced retainers.',
    icon: Globe,
    color: 'purple'
  },
  {
    id: 'client_booking_portal',
    category: 'competitive',
    title: 'Integrated /book Portal & Auto-Agreements',
    badge: 'Zero Add-On Fees',
    metric: '$180/yr Saved/Seat',
    description: 'Self-service client appointment scheduling with two-way Google Calendar sync that instantly generates contracts and invoices upon confirmation.',
    pilotBenefit: 'Eliminates standalone scheduling tool subscriptions (Calendly/Acuity) and manual contract drafting for every founder.',
    icon: Calendar,
    color: 'blue'
  },
  {
    id: 'sovereign_vault',
    category: 'security',
    title: '0.05s Air-Gapped Reconciler & Vault',
    badge: 'Zero Password Scraping',
    metric: '0.05s Statement Parse',
    description: 'Ingest bank PDFs or CSVs with local encryption. Categorizes deductions in sub-seconds with zero credential sharing or telemetry harvesting.',
    pilotBenefit: 'Enterprise and institutional sponsors maintain strict compliance with POPIA, GDPR, and banking security mandates.',
    icon: Lock,
    color: 'amber'
  },
  {
    id: 'cpa_audit_hub',
    category: 'competitive',
    title: '1-Click CPA & Fiscal Export Hub',
    badge: 'Audit Preparedness',
    metric: '< 60s Tax Export',
    description: 'Structured balance ledger exports (JSON, CSV, PDF) and tax reserve audit trails for frictionless accountant and mentor handoffs.',
    pilotBenefit: 'Accelerates cohort year-end review cycles and simplifies grant/lender reporting for program directors.',
    icon: Landmark,
    color: 'rose'
  },
  {
    id: 'multilingual_reach',
    category: 'institutions',
    title: 'Full 9-Language Native Localization',
    badge: 'Pan-Continental Inclusivity',
    metric: '9 Native Languages',
    description: 'Complete native localization in English, Spanish, French, German, Portuguese, Hindi, Swahili, isiZulu, and Afrikaans across every workspace, modal, and tax calculation matrix.',
    pilotBenefit: 'Empowers diverse and emerging market grassroots entrepreneurship cohorts without language barrier friction.',
    icon: Users,
    color: 'cyan'
  },
  {
    id: 'byok_ai_cofounder',
    category: 'competitive',
    title: 'Private BYOK AI Co-Founder & Chief of Staff',
    badge: 'Zero API Markups',
    metric: '8 Frontier Engines',
    description: 'Autonomous AI co-founder for pricing audits, contract drafting, and runway forecasts with 8 top engines (Gemini, Claude, GPT-4o, Groq, DeepSeek, Mistral, Perplexity, OpenRouter) and zero markups.',
    pilotBenefit: 'Founders connect their own keys to 8 top frontier models with complete data privacy, sovereign encryption, and zero middleman markups.',
    icon: Cpu,
    color: 'teal'
  }
];

export function WhatIsAzuraOSModal({
  isOpen,
  onClose,
  onStartTrial,
  onRequestDemo,
  onOpenTour,
  onNavigateToPricing,
  initialTab = 'pitch'
}: WhatIsAzuraOSModalProps) {
  const [activeTab, setActiveTab] = useState<'pitch' | 'capabilities' | 'pilot_program'>(initialTab);
  const [highlightCategory, setHighlightCategory] = useState<'all' | 'institutions' | 'competitive' | 'retention' | 'security'>('all');
  const [selectedCohortSize, setSelectedCohortSize] = useState<number>(25);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [demoInquiryType, setDemoInquiryType] = useState<DemoInquiryType>('vip_1on1');
  const [isTourModalOpen, setIsTourModalOpen] = useState(false);

  React.useEffect(() => {
    if (isOpen && initialTab) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  if (!isOpen) return null;

  const handleOpenDemoModal = (type: DemoInquiryType = 'vip_1on1') => {
    if (onRequestDemo) {
      onClose();
      onRequestDemo();
    } else {
      setDemoInquiryType(type);
      setIsDemoModalOpen(true);
    }
  };

  const handleOpenTour = () => {
    if (onOpenTour) {
      onClose();
      onOpenTour();
    } else {
      setIsTourModalOpen(true);
    }
  };

  const handleStartTrial = () => {
    onClose();
    if (onStartTrial) {
      onStartTrial();
    }
  };

  const filteredHighlights = highlightCategory === 'all' 
    ? PILOT_HIGHLIGHTS 
    : PILOT_HIGHLIGHTS.filter(h => h.category === highlightCategory);

  // Dynamic ROI calculation metrics based on cohort size
  const hoursSavedPerMonth = selectedCohortSize * 16;
  const projectedSurvivingBusinesses = Math.round(selectedCohortSize * 0.92);
  const estimatedAnnualInvoiced = selectedCohortSize * 45000;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/85 backdrop-blur-md transition-opacity animate-fade-in"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative w-full max-w-4xl max-h-[92vh] bg-[#0C0E0F] border border-[#2A2F31] rounded-3xl shadow-2xl flex flex-col overflow-hidden z-10 animate-in fade-in zoom-in-95 duration-200 text-gray-200">
        
        {/* Sticky Header */}
        <div className="sticky top-0 z-30 bg-[#121516]/95 backdrop-blur-md border-b border-[#1F2426] px-5 sm:px-8 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#14B8A6]/10 border border-[#14B8A6]/30 flex items-center justify-center text-[#14B8A6]">
              <Building2 size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-display font-black text-lg sm:text-xl text-white tracking-tight leading-none">
                  AzuraOS System Briefing
                </h2>
                <span className="text-[9px] text-[#14B8A6] font-mono tracking-wider uppercase px-2 py-0.5 rounded bg-[#14B8A6]/15 border border-[#14B8A6]/30">
                  Chief of Staff OS
                </span>
              </div>
              <p className="text-xs text-gray-400 font-medium mt-0.5">
                Autonomous Chief of Staff &amp; CFO Executive Suite for the 1-Person Unicorn
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleStartTrial}
              className="hidden sm:flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#14B8A6] hover:bg-[#0EA5A4] text-black text-xs font-bold transition-all shadow-md active:scale-95 cursor-pointer"
            >
              <span>Deploy Chief of Staff</span>
              <ArrowRight size={13} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white rounded-xl hover:bg-[#1F2426] border border-transparent hover:border-[#2A2F31] transition-all cursor-pointer"
              title="Close modal"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Navigation Tabs Bar */}
        <div className="bg-[#151819] border-b border-[#1F2426] px-5 sm:px-8 py-2 flex items-center gap-2 overflow-x-auto custom-scrollbar">
          {[
            { id: 'pitch', label: '1. Autonomous Chief of Staff & Solopreneur Edge', icon: Sparkles },
            { id: 'capabilities', label: '2. Two Executive Engines (Command & CFO Suite)', icon: Layers },
            { id: 'pilot_program', label: '3. Institutional Cohort Pilots & Solvency Defense', icon: Landmark },
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                  isActive
                    ? 'bg-[#14B8A6] text-black shadow-md'
                    : 'text-gray-400 hover:text-white hover:bg-[#1F2426]'
                }`}
              >
                <Icon size={14} className={isActive ? 'text-black' : 'text-teal-400'} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Scrollable Content Body */}
        <div className="overflow-y-auto p-5 sm:p-8 space-y-8 custom-scrollbar">
          
          {/* TAB 1: EXECUTIVE BRIEFING & TRANSFORMATION */}
          {activeTab === 'pitch' && (
            <div className="space-y-8 animate-fade-in text-left">
              {/* Hero Hook */}
              <div className="text-center space-y-4 max-w-2xl mx-auto">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#14B8A6]/10 border border-[#14B8A6]/30 text-[#14B8A6] text-xs font-mono font-bold uppercase tracking-wider">
                  <Activity size={13} className="animate-pulse" />
                  <span>Global Solvency Engine &amp; Multi-Jurisdiction Radar</span>
                </div>

                <h1 className="font-display font-black text-2xl sm:text-4xl text-white tracking-tight leading-tight">
                  Automate Statutory Tax Reserves. <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#14B8A6] via-teal-300 to-emerald-400">
                    Defend Real Operating Margins.
                  </span>
                </h1>

                <p className="text-sm sm:text-base text-gray-300 leading-relaxed font-light">
                  A sovereign financial operating system built to eliminate cash flow blind spots for borderless operators and institutional cohorts across <strong className="text-white">6 global currencies (USD, ZAR, GBP, EUR, AUD, INR)</strong>.
                </p>

                <div className="bg-[#151819] border border-[#2A2F31] rounded-2xl p-4 sm:p-5 text-left space-y-2.5 shadow-md">
                  <div className="flex items-start gap-2.5 text-xs sm:text-sm text-rose-300">
                    <span className="text-rose-400 font-bold shrink-0 mt-0.5">❌</span>
                    <span><strong>The Old Way:</strong> 5–7 disconnected tools (Calendly, DocuSign, Xero, Notion, messy spreadsheets) + reactive accounting that discovers tax deficits months too late.</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs sm:text-sm text-teal-300 font-medium">
                    <span className="text-[#14B8A6] font-bold shrink-0 mt-0.5">✅</span>
                    <span><strong>With AzuraOS:</strong> 1 unified execution layer that ring-fences statutory taxes at contract inception, enforces 50% deposits, and reconciles air-gapped in 0.05s.</span>
                  </div>
                </div>
              </div>

              {/* WHO AZURAOS IS DESIGNED FOR — 3 CORE ARCHETYPES */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <Users size={16} className="text-[#14B8A6]" />
                    <span>Who AzuraOS is Built For</span>
                  </h3>
                  <span className="text-[11px] font-mono text-gray-400">3 Core Ecosystem Archetypes</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Archetype 1 */}
                  <div className="p-4 rounded-2xl bg-[#121516] border border-[#2A2F31] space-y-2 hover:border-[#14B8A6]/40 transition-colors">
                    <div className="w-8 h-8 rounded-xl bg-teal-500/10 text-teal-400 flex items-center justify-center font-bold text-sm">
                      <Briefcase size={16} />
                    </div>
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-bold text-white">Independent Operators &amp; Studios</h4>
                      <span className="text-[10px] font-mono bg-teal-500/10 text-teal-300 px-2 py-0.5 rounded">Solo / Team</span>
                    </div>
                    <p className="text-xs text-gray-400 leading-relaxed">
                      Consultants, creators, and agency owners billing locally or cross-border. Replaces tool sprawl with instant contracts, 50% milestone deposits, and safe owner dividends.
                    </p>
                  </div>

                  {/* Archetype 2 */}
                  <div className="p-4 rounded-2xl bg-[#121516] border border-[#2A2F31] space-y-2 hover:border-[#14B8A6]/40 transition-colors">
                    <div className="w-8 h-8 rounded-xl bg-purple-500/10 text-purple-400 flex items-center justify-center font-bold text-sm">
                      <Landmark size={16} />
                    </div>
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-bold text-white">Accelerators &amp; Incubators</h4>
                      <span className="text-[10px] font-mono bg-purple-500/10 text-purple-300 px-2 py-0.5 rounded">Cohorts</span>
                    </div>
                    <p className="text-xs text-gray-400 leading-relaxed">
                      Program directors wanting standardized financial hygiene across 20–50 batch founders. Provides real-time runway visibility and eliminates post-demo day insolvency.
                    </p>
                  </div>

                  {/* Archetype 3 */}
                  <div className="p-4 rounded-2xl bg-[#121516] border border-[#2A2F31] space-y-2 hover:border-[#14B8A6]/40 transition-colors">
                    <div className="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-400 flex items-center justify-center font-bold text-sm">
                      <Building2 size={16} />
                    </div>
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-bold text-white">Enterprise ESD &amp; Banking Hubs</h4>
                      <span className="text-[10px] font-mono bg-amber-500/10 text-amber-300 px-2 py-0.5 rounded">Enterprise</span>
                    </div>
                    <p className="text-xs text-gray-400 leading-relaxed">
                      Corporate supplier development programs and SME commercial lenders requiring verified solvency diagnostics, statutory compliance, and grant audit reports.
                    </p>
                  </div>
                </div>
              </div>

              {/* Conversion Value Pillar Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 rounded-2xl bg-[#121516] border border-[#2A2F31] space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-teal-500/10 text-teal-400 flex items-center justify-center font-bold text-sm">
                    <TrendingUp size={16} />
                  </div>
                  <h4 className="text-sm font-bold text-white">Live Margin &amp; Solvency Radar</h4>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Sub-second math partitions VAT and statutory tax holding reserves, showing your exact <em>Net Safe Spendable Owner Dividend</em> on every booking.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-[#121516] border border-[#2A2F31] space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-400 flex items-center justify-center font-bold text-sm">
                    <Receipt size={16} />
                  </div>
                  <h4 className="text-sm font-bold text-white">Invoicing &amp; 50% Deposits</h4>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Generate professional tax invoices with built-in 50% milestone deposits and automated follow-ups without leaving the system.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-[#121516] border border-[#2A2F31] space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center font-bold text-sm">
                    <ShieldCheck size={16} />
                  </div>
                  <h4 className="text-sm font-bold text-white">0.05s Air-Gapped Reconciler</h4>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Drop standard bank PDFs or CSVs to reconcile deductions with 1-click review and zero third-party password scraping.
                  </p>
                </div>
              </div>

              {/* Dedicated Unregistered / Sole Proprietor Welcome Banner */}
              <div className="bg-gradient-to-br from-[#151E20] via-[#151819] to-[#0D1517] border-2 border-[#14B8A6]/40 rounded-3xl p-6 sm:p-7 space-y-4 shadow-xl text-left relative overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#2A2F31] pb-3">
                  <div className="flex items-center gap-2">
                    <span className="p-2 rounded-xl bg-[#14B8A6]/15 text-[#14B8A6] border border-[#14B8A6]/30">
                      <Target size={16} />
                    </span>
                    <span className="text-xs font-mono font-bold text-[#14B8A6] uppercase tracking-wider">
                      Zero Entity Registration Barrier
                    </span>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-[#14B8A6]/20 border border-[#14B8A6]/40 text-[#14B8A6] text-[10px] font-mono font-bold uppercase">
                    Day 1 Operational Readiness
                  </span>
                </div>

                <div className="space-y-1.5">
                  <h3 className="font-display font-extrabold text-xl text-white tracking-tight">
                    Running as a Solo Freelancer or Sole Proprietor?
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-300 leading-relaxed font-light">
                    You do not need an incorporated enterprise, a corporate tax ID, or an expensive retainer accountant to maintain immaculate financial discipline. AzuraOS gives you world-class operational governance and audit-ready paper trails from your very first client invoice.
                  </p>
                </div>
              </div>

              {/* Quick Action Bar */}
              <div className="p-5 rounded-2xl bg-[#151819] border border-[#2A2F31] flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h4 className="text-sm font-bold text-white">Experience AzuraOS in action right now</h4>
                  <p className="text-xs text-gray-400">Try our interactive tour or initiate your 7-day full access pass.</p>
                </div>
                <div className="flex items-center gap-2.5 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={handleOpenTour}
                    className="flex-1 sm:flex-initial px-4 py-2.5 rounded-xl bg-[#1F2426] hover:bg-[#2A2F31] text-teal-300 font-bold text-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Play size={13} />
                    <span>60s Product Tour</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleStartTrial}
                    className="flex-1 sm:flex-initial px-5 py-2.5 rounded-xl bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs transition-all shadow-md flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <span>Start 7-Day Free Pass</span>
                    <ArrowRight size={13} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: CORE PLATFORM SUITE (8 WORKSPACES) */}
          {activeTab === 'capabilities' && (
            <div className="space-y-6 text-left animate-fade-in">
              <div>
                <h3 className="font-display font-bold text-xl text-white">
                  The Complete 8-Workspace Platform Architecture
                </h3>
                <p className="text-xs text-gray-400 mt-1">
                  Everything you need to operate a profitable business with zero subscription bloat.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                
                {/* Module 1 */}
                <div className="p-4 bg-[#151819] border border-[#2A2F31] rounded-2xl space-y-2 hover:border-[#14B8A6]/40 transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-[#14B8A6] flex items-center gap-1.5">
                      <TrendingUp size={14} />
                      <span>1. Real-Time Margin &amp; Profit Engine</span>
                    </span>
                    <span className="px-2 py-0.5 bg-teal-500/10 text-teal-300 text-[10px] rounded font-mono">Live Sync</span>
                  </div>
                  <p className="text-xs text-gray-300 leading-relaxed">
                    Dynamic unit economics tracking. Automatically recalculates gross and net margins on every booking, deliverable, and operating cost.
                  </p>
                </div>

                {/* Module 2 */}
                <div className="p-4 bg-[#151819] border border-[#2A2F31] rounded-2xl space-y-2 hover:border-[#14B8A6]/40 transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-[#14B8A6] flex items-center gap-1.5">
                      <Receipt size={14} />
                      <span>2. Invoicing &amp; 50% Deposit Milestones</span>
                    </span>
                    <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-300 text-[10px] rounded font-mono">PDF &amp; Status</span>
                  </div>
                  <p className="text-xs text-gray-300 leading-relaxed">
                    Generate crisp, professional PDF invoices with client payment status, partial deposit milestones, and direct banking/payment details.
                  </p>
                </div>

                {/* Module 3 */}
                <div className="p-4 bg-[#151819] border border-[#2A2F31] rounded-2xl space-y-2 hover:border-[#14B8A6]/40 transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-[#14B8A6] flex items-center gap-1.5">
                      <Calendar size={14} />
                      <span>3. Client Self-Service Booking Portal</span>
                    </span>
                    <span className="px-2 py-0.5 bg-blue-500/10 text-blue-300 text-[10px] rounded font-mono">/book + Calendar</span>
                  </div>
                  <p className="text-xs text-gray-300 leading-relaxed">
                    Public client scheduling interface with automated confirmation workflows and two-way Google Calendar integration.
                  </p>
                </div>

                {/* Module 4 */}
                <div className="p-4 bg-[#151819] border border-[#2A2F31] rounded-2xl space-y-2 hover:border-[#14B8A6]/40 transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-[#14B8A6] flex items-center gap-1.5">
                      <Globe size={14} />
                      <span>4. Multi-Jurisdiction Global Tax Engine</span>
                    </span>
                    <span className="px-2 py-0.5 bg-purple-500/10 text-purple-300 text-[10px] rounded font-mono">6 Currencies &amp; Tax Regimes</span>
                  </div>
                  <p className="text-xs text-gray-300 leading-relaxed">
                    Pre-calibrated statutory holding engines for US (IRS), UK (HMRC), South Africa (SARS), EU (VAT), Australia (ATO GST), and India (GST/44ADA) across 6 native currencies (USD, ZAR, GBP, EUR, AUD, INR).
                  </p>
                </div>

                {/* Module 5 */}
                <div className="p-4 bg-[#151819] border border-[#2A2F31] rounded-2xl space-y-2 hover:border-[#14B8A6]/40 transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-[#14B8A6] flex items-center gap-1.5">
                      <Lock size={14} />
                      <span>5. Sovereign Local-First Vault</span>
                    </span>
                    <span className="px-2 py-0.5 bg-amber-500/10 text-amber-300 text-[10px] rounded font-mono">AES-256 &amp; Privacy</span>
                  </div>
                  <p className="text-xs text-gray-300 leading-relaxed">
                    Zero third-party data harvesting or commercial telemetry. Encrypted storage with client-side passphrase protection options.
                  </p>
                </div>

                {/* Module 6 */}
                <div className="p-4 bg-[#151819] border border-[#2A2F31] rounded-2xl space-y-2 hover:border-[#14B8A6]/40 transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-[#14B8A6] flex items-center gap-1.5">
                      <Cpu size={14} />
                      <span>6. Private AI Co-Founder (Strictly BYOK)</span>
                    </span>
                    <span className="px-2 py-0.5 bg-cyan-500/10 text-cyan-300 text-[10px] rounded font-mono">Google Gemini</span>
                  </div>
                  <p className="text-xs text-gray-300 leading-relaxed">
                    Direct integration with Google AI Studio. Run strategic simulations, contract drafts, and cash flow advice without middleman API markups.
                  </p>
                </div>

                {/* Module 7 */}
                <div className="p-4 bg-[#151819] border border-[#2A2F31] rounded-2xl space-y-2 hover:border-[#14B8A6]/40 transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-[#14B8A6] flex items-center gap-1.5">
                      <Landmark size={14} />
                      <span>7. 1-Click CPA &amp; Accountant Audit Hub</span>
                    </span>
                    <span className="px-2 py-0.5 bg-rose-500/10 text-rose-300 text-[10px] rounded font-mono">Fiscal Summaries</span>
                  </div>
                  <p className="text-xs text-gray-300 leading-relaxed">
                    Dedicated Accountant View with audit trails, tax reserve ledger, and 1-click structured balance exports (CSV &amp; JSON).
                  </p>
                </div>

                {/* Module 8 */}
                <div className="p-4 bg-[#151819] border border-[#2A2F31] rounded-2xl space-y-2 hover:border-[#14B8A6]/40 transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-[#14B8A6] flex items-center gap-1.5">
                      <Users size={14} />
                      <span>8. Multi-Lingual Global Access (EN &amp; HI)</span>
                    </span>
                    <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-300 text-[10px] rounded font-mono">100% Hindi/English</span>
                  </div>
                  <p className="text-xs text-gray-300 leading-relaxed">
                    Full internationalization across all workspaces, allowing global entrepreneurs and emerging market founders to operate in their language.
                  </p>
                </div>

              </div>

              {/* Callout */}
              <div className="p-4 rounded-2xl bg-[#121516] border border-[#14B8A6]/30 flex items-center justify-between gap-4">
                <div className="text-xs text-gray-300">
                  <strong className="text-white">Ready to streamline your operational stack?</strong> Get immediate access to all 8 workspaces today.
                </div>
                <button
                  type="button"
                  onClick={handleStartTrial}
                  className="px-4 py-2 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs rounded-xl transition-all cursor-pointer whitespace-nowrap"
                >
                  Start 7-Day Trial
                </button>
              </div>
            </div>
          )}

          {/* TAB 3: INSTITUTIONAL PILOT PROGRAM & HIGHLIGHTS */}
          {activeTab === 'pilot_program' && (
            <div className="space-y-8 text-left animate-fade-in">
              
              {/* Header Hero Banner */}
              <div className="p-6 sm:p-7 bg-gradient-to-r from-teal-500/15 via-[#151819] to-cyan-500/10 border-2 border-[#14B8A6]/40 rounded-3xl space-y-3">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 rounded-lg bg-[#14B8A6]/20 text-[#14B8A6] text-[10px] font-mono font-bold uppercase tracking-wider border border-[#14B8A6]/40">
                    INSTITUTIONAL COHORT PROGRAM
                  </span>
                  <span className="text-xs text-gray-400">• For Accelerators, ESD Programs &amp; Enterprise Sponsors</span>
                </div>
                <h3 className="font-display font-black text-2xl sm:text-3xl text-white tracking-tight leading-tight">
                  Eliminate the #1 cause of post-cohort bankruptcy: <br className="hidden sm:inline" />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#14B8A6] via-teal-300 to-emerald-400">
                    spending tax money as runway.
                  </span>
                </h3>
                <p className="text-xs sm:text-sm text-gray-300 leading-relaxed font-light max-w-3xl">
                  Accelerators and ESD programs lose portfolio companies not from lack of ambition, but from blind runway management. AzuraOS provides live, forward-looking solvency tracking across your entire batch—preventing tax surprises and cash crunches before they happen.
                </p>
              </div>

              {/* DYNAMIC PILOT HIGHLIGHTS SECTION */}
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h4 className="text-base font-bold text-white flex items-center gap-2">
                      <Sparkles size={16} className="text-[#14B8A6]" />
                      <span>Key Competitive Advantages &amp; Pilot Readiness</span>
                    </h4>
                    <p className="text-xs text-gray-400">
                      Why institutional sponsors, incubator leads, and enterprise accelerators choose AzuraOS.
                    </p>
                  </div>

                  {/* Filter Pills */}
                  <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
                    {[
                      { id: 'all', label: 'All Advantages' },
                      { id: 'institutions', label: 'Institutional Ready' },
                      { id: 'retention', label: 'Runway Defense' },
                      { id: 'competitive', label: 'Zero Bloat' },
                      { id: 'security', label: 'Data Sovereignty' }
                    ].map(f => (
                      <button
                        key={f.id}
                        onClick={() => setHighlightCategory(f.id as any)}
                        className={`px-3 py-1.5 rounded-xl text-[11px] font-bold transition-all whitespace-nowrap cursor-pointer ${
                          highlightCategory === f.id
                            ? 'bg-[#14B8A6] text-black shadow-sm'
                            : 'bg-[#151819] text-gray-400 hover:text-white border border-[#2A2F31]'
                        }`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Highlights Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                  {filteredHighlights.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div 
                        key={item.id}
                        className="p-4 bg-[#151819] border border-[#2A2F31] hover:border-[#14B8A6]/50 rounded-2xl space-y-3 transition-all group"
                      >
                        <div className="space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2.5 min-w-0 flex-1">
                              <div className="w-8 h-8 rounded-xl bg-teal-500/10 border border-teal-500/20 text-[#14B8A6] flex items-center justify-center shrink-0">
                                <Icon size={16} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h5 className="text-xs sm:text-sm font-bold text-white group-hover:text-[#14B8A6] transition-colors leading-snug">
                                  {item.title}
                                </h5>
                                <span className="text-[10px] font-mono text-gray-400 block mt-0.5">
                                  {item.badge}
                                </span>
                              </div>
                            </div>
                            <span className="hidden sm:inline-flex px-2 py-0.5 rounded-full bg-teal-500/15 text-teal-300 text-[10px] font-mono font-extrabold border border-teal-500/30 whitespace-nowrap shrink-0">
                              {item.metric}
                            </span>
                          </div>
                          
                          {/* Mobile metric badge */}
                          <div className="sm:hidden flex items-center justify-end">
                            <span className="px-2 py-0.5 rounded-full bg-teal-500/15 text-teal-300 text-[9px] font-mono font-extrabold border border-teal-500/30 whitespace-nowrap">
                              {item.metric}
                            </span>
                          </div>
                        </div>

                        <p className="text-xs text-gray-300 leading-relaxed font-light">
                          {item.description}
                        </p>

                        <div className="p-2.5 rounded-xl bg-[#0F1213] border border-[#1F2426] text-[11px] text-teal-300/90 leading-snug flex items-start gap-2">
                          <CheckCircle2 size={13} className="text-[#14B8A6] shrink-0 mt-0.5" />
                          <span><strong>Pilot Value:</strong> {item.pilotBenefit}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* DYNAMIC COHORT ROI & IMPACT SIMULATOR */}
              <div className="bg-gradient-to-br from-[#121618] via-[#151819] to-[#0E1517] border-2 border-teal-500/30 rounded-3xl p-4 sm:p-6 space-y-5 shadow-xl overflow-hidden">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#2A2F31] pb-4">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <Calculator size={18} className="text-[#14B8A6] shrink-0" />
                      <h4 className="text-sm sm:text-base font-bold text-white">
                        Institutional Pilot Cohort Impact Calculator
                      </h4>
                    </div>
                    <p className="text-xs text-gray-400">
                      Select your target cohort size to project operational hours saved and cash flow protection.
                    </p>
                  </div>

                  {/* Cohort Size Quick Selector */}
                  <div className="grid grid-cols-4 sm:flex items-center gap-1.5 bg-[#0C0E0F] p-1.5 rounded-xl border border-[#2A2F31] w-full sm:w-auto shrink-0">
                    {[10, 25, 50, 100].map(size => (
                      <button
                        key={size}
                        onClick={() => setSelectedCohortSize(size)}
                        className={`px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-mono font-bold transition-all text-center cursor-pointer ${
                          selectedCohortSize === size
                            ? 'bg-[#14B8A6] text-black font-extrabold shadow-sm'
                            : 'text-gray-400 hover:text-white'
                        }`}
                      >
                        {size} Seats
                      </button>
                    ))}
                  </div>
                </div>

                {/* Dynamically Calculated Metric Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                  <div className="p-4 rounded-2xl bg-[#121516] border border-[#2A2F31] space-y-1">
                    <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider block">
                      Admin Hours Saved / Month
                    </span>
                    <div className="text-xl sm:text-2xl font-black text-white font-mono flex items-center gap-1">
                      <span className="text-[#14B8A6]">~{hoursSavedPerMonth.toLocaleString()}</span>
                      <span className="text-xs text-gray-400 font-sans font-normal">hrs</span>
                    </div>
                    <p className="text-[11px] text-gray-400 leading-tight">
                      Saved on manual invoicing, spreadsheet reconciling, and calendar chasing.
                    </p>
                  </div>

                  <div className="p-4 rounded-2xl bg-[#121516] border border-[#2A2F31] space-y-1">
                    <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider block">
                      Projected Viability Rate
                    </span>
                    <div className="text-xl sm:text-2xl font-black text-emerald-400 font-mono flex items-center gap-1">
                      <span>92%</span>
                      <span className="text-xs text-gray-400 font-sans font-normal">({projectedSurvivingBusinesses}/{selectedCohortSize} active)</span>
                    </div>
                    <p className="text-[11px] text-gray-400 leading-tight">
                      Enforcing 50% upfront deposits and live margin runway defense.
                    </p>
                  </div>

                  <div className="p-4 rounded-2xl bg-[#121516] border border-[#2A2F31] space-y-1">
                    <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider block">
                      Audit Readiness
                    </span>
                    <div className="text-xl sm:text-2xl font-black text-purple-400 font-mono">
                      100%
                    </div>
                    <p className="text-[11px] text-gray-400 leading-tight">
                      Instant fiscal year CSV/JSON export ready for CPAs and grant evaluators.
                    </p>
                  </div>
                </div>

                {/* Direct Action for Calculated Cohort */}
                <div className="p-4 rounded-2xl bg-[#0D1011] border border-teal-500/20 flex flex-col sm:flex-row items-center justify-between gap-3">
                  <div className="text-xs text-gray-300 text-center sm:text-left">
                    <strong className="text-white">Ready to deploy for a {selectedCohortSize}-founder cohort?</strong> Includes turnkey onboarding playbook and dedicated sponsor audit desk.
                  </div>
                  <button
                    type="button"
                    onClick={() => handleOpenDemoModal('bank_pilot')}
                    className="w-full sm:w-auto px-4 py-2 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs rounded-xl transition-all shadow-md flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap"
                  >
                    <span>Reserve {selectedCohortSize}-Seat Pilot</span>
                    <ArrowRight size={13} />
                  </button>
                </div>
              </div>

              {/* COMPETITIVE MATRIX COMPARISON */}
              <div className="bg-[#151819] border border-[#2A2F31] rounded-3xl p-5 sm:p-6 space-y-4">
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-white flex items-center gap-2">
                    <Award size={16} className="text-[#14B8A6]" />
                    <span>AzuraOS vs. Heavy Legacy Accounting &amp; Disconnected Spreadsheets</span>
                  </h4>
                  <p className="text-xs text-gray-400">
                    Why traditional accounting software leads to 82% solopreneur failure, and how AzuraOS fixes it.
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-[#2A2F31] text-[11px] font-mono text-gray-400 uppercase">
                        <th className="py-2.5 px-3">Dimension</th>
                        <th className="py-2.5 px-3 text-[#14B8A6] bg-teal-500/10 rounded-t-xl font-bold">AzuraOS Pilot</th>
                        <th className="py-2.5 px-3">Xero / QuickBooks</th>
                        <th className="py-2.5 px-3">Manual Excel Sheets</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#1F2426] text-gray-300 font-light">
                      <tr>
                        <td className="py-2.5 px-3 font-semibold text-white">Time to First Live Invoice</td>
                        <td className="py-2.5 px-3 text-teal-300 bg-teal-500/5 font-bold">&lt; 3 Minutes</td>
                        <td className="py-2.5 px-3 text-gray-400">2-3 Weeks (Chart of Accts setup)</td>
                        <td className="py-2.5 px-3 text-gray-400">Manual template design</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 px-3 font-semibold text-white">Live Margin Defense</td>
                        <td className="py-2.5 px-3 text-teal-300 bg-teal-500/5 font-bold">Live on every line item</td>
                        <td className="py-2.5 px-3 text-gray-400">Lagged monthly P&amp;L review</td>
                        <td className="py-2.5 px-3 text-gray-400">Error-prone broken formulas</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 px-3 font-semibold text-white">50% Deposit Safeguard</td>
                        <td className="py-2.5 px-3 text-teal-300 bg-teal-500/5 font-bold">Automated 1-Click Enforcement</td>
                        <td className="py-2.5 px-3 text-gray-400">Complex manual progress invoicing</td>
                        <td className="py-2.5 px-3 text-gray-400">Untracked cash receivables</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 px-3 font-semibold text-white">Client Booking Portal (/book)</td>
                        <td className="py-2.5 px-3 text-teal-300 bg-teal-500/5 font-bold">Included Native</td>
                        <td className="py-2.5 px-3 text-gray-400">Requires $15/mo Calendly plugin</td>
                        <td className="py-2.5 px-3 text-gray-400">None (Email back-and-forth)</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 px-3 font-semibold text-white">Data Privacy &amp; Telemetry</td>
                        <td className="py-2.5 px-3 text-teal-300 bg-teal-500/5 font-bold">Sovereign / Zero Harvesting</td>
                        <td className="py-2.5 px-3 text-gray-400">Commercial cloud profiling</td>
                        <td className="py-2.5 px-3 text-gray-400">Local unencrypted files</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 30-Day Pilot Blueprint */}
              <div className="bg-[#121516] border border-[#2A2F31] rounded-2xl p-5 space-y-4">
                <h4 className="text-sm font-bold text-white uppercase tracking-wider font-mono flex items-center gap-2">
                  <Clock size={16} className="text-[#14B8A6]" />
                  <span>The 30-Day Institutional Pilot Rollout Blueprint</span>
                </h4>
                
                <div className="space-y-3 text-xs">
                  <div className="flex items-start gap-3 p-3 bg-[#151819] rounded-xl border border-[#1F2426]">
                    <span className="w-6 h-6 rounded-full bg-[#14B8A6]/20 text-[#14B8A6] font-bold flex items-center justify-center shrink-0">1</span>
                    <div>
                      <strong className="text-white">Week 1 — Turnkey Cohort Provisioning:</strong>
                      <p className="text-gray-400 mt-0.5">Bulk account onboarding for cohort founders. Instant configuration of local tax presets, currency formatting, and branding.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-[#151819] rounded-xl border border-[#1F2426]">
                    <span className="w-6 h-6 rounded-full bg-[#14B8A6]/20 text-[#14B8A6] font-bold flex items-center justify-center shrink-0">2</span>
                    <div>
                      <strong className="text-white">Weeks 2 &amp; 3 — Operational Execution:</strong>
                      <p className="text-gray-400 mt-0.5">Founders issue live client invoices with 50% deposit milestones, self-manage booking slots, and track real-time margin defense.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-[#151819] rounded-xl border border-[#1F2426]">
                    <span className="w-6 h-6 rounded-full bg-[#14B8A6]/20 text-[#14B8A6] font-bold flex items-center justify-center shrink-0">3</span>
                    <div>
                      <strong className="text-white">Week 4 — Institutional Health Summary:</strong>
                      <p className="text-gray-400 mt-0.5">Generate comprehensive cohort financial viability scorecards, tax readiness audit logs, and sponsor ROI reports.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Official Procurement Pack Request Card */}
              <div className="p-6 rounded-2xl bg-gradient-to-r from-[#151819] via-[#12181A] to-[#121E20] border-2 border-[#14B8A6]/40 flex flex-col sm:flex-row items-center justify-between gap-5">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="text-[#14B8A6]" />
                    <h4 className="text-sm font-bold text-white">Official Procurement Pack &amp; Pilot RFP</h4>
                  </div>
                  <p className="text-xs text-gray-300 max-w-lg leading-relaxed">
                    Request our complete enterprise specification memo, data sovereignty audit pack, cohort volume pricing sheet, and legal terms for investment committees.
                  </p>
                </div>
                <div className="flex items-center gap-2.5 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => handleOpenDemoModal('bank_pilot')}
                    className="flex-1 sm:flex-initial px-5 py-3 rounded-xl bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap active:scale-95"
                  >
                    <Landmark size={14} />
                    <span>Request Cohort Pilot Proposal</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Google Cloud Partner Advantage Member Banner */}
          <GoogleCloudPartnerBadge variant="banner" />

          {/* Bottom Action Footer */}
          <div className="p-6 rounded-3xl bg-[#121516] border border-[#1F2426] text-center space-y-4">
            <h4 className="text-base sm:text-lg font-bold text-white">Ready to eliminate the admin chaos and run your business from one system?</h4>
            <p className="text-xs text-gray-400 max-w-lg mx-auto">
              Start with a 7-day full access pass or request an institutional pilot consultation. Lock in your early operator benefits.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 pt-1">
              <button
                type="button"
                onClick={handleStartTrial}
                className="py-3 px-6 rounded-xl bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs transition-all shadow-lg active:scale-95 cursor-pointer flex items-center gap-2"
              >
                <Sparkles size={14} />
                <span>Start 7-Day Free Trial</span>
              </button>
              <button
                type="button"
                onClick={() => handleOpenDemoModal('bank_pilot')}
                className="py-3 px-5 rounded-xl bg-[#151819] hover:bg-[#1F2426] border border-[#2A2F31] text-gray-200 font-semibold text-xs transition-all cursor-pointer flex items-center gap-2"
              >
                <Landmark size={14} className="text-teal-400" />
                <span>Request Institutional Pilot</span>
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Embedded Sub-Modals */}
      <RequestDemoModal
        isOpen={isDemoModalOpen}
        onClose={() => setIsDemoModalOpen(false)}
        defaultInquiryType={demoInquiryType}
        onOpenInteractiveTour={() => {
          setIsDemoModalOpen(false);
          setIsTourModalOpen(true);
        }}
      />

      <InteractiveTourModal
        isOpen={isTourModalOpen}
        onClose={() => setIsTourModalOpen(false)}
        onStartTrial={() => {
          setIsTourModalOpen(false);
          handleStartTrial();
        }}
        onRequestVipDemo={() => {
          setIsTourModalOpen(false);
          handleOpenDemoModal('vip_1on1');
        }}
      />
    </div>
  );
}
